<template>
  <div class="home detail-actions-page">
    <div class="px-8 py-4">
      <div class="detail-page-header mb-4">
        <button type="button" class="btn btn-link text-decoration-none detail-page-back" @click="$router.go(-1)" style="padding: 10px 0">
          <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-2" />{{$t('back')}}
        </button>
        <div class="detail-page-title-row d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
          <div class="d-flex flex-wrap align-items-center gap-2">
            <h3 class="detail-page-title mb-0">{{ $t('transaction') }} #{{ transaction?.reference || '-' }}</h3>
            <span v-if="transaction.payment_status" class="badge" :class="transaction.payment_status === 'paid' ? 'bg-success' : (transaction.payment_status === 'pending' ? 'bg-warning text-dark' : 'bg-danger')">
              {{ $t(transaction.payment_status) }}
            </span>
          </div>
          <div class="detail-page-actions">
            <button v-if="transaction.payment_status === 'paid'" @click="printReceipt" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs">
              <AppIcon name="bi-printer-fill" class="bi bi-printer-fill" style="font-size:12px" /> {{ $t('print') }}
            </button>
            <button v-if="transaction.payment_status === 'paid' && helper.checkAccess('create_invoices') > 0" :disabled="loading" @click="transferToInvoice" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs detail-page-action--secondary">
              <AppIcon name="bi-arrow-left-right" class="bi bi-arrow-left-right" style="font-size:12px" /> {{ $t('invoice') }}
            </button>
            <button v-if="transaction.payment_status !== 'canceled'" @click="confirmCancelTransaction" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs detail-page-action--danger">
              <AppIcon name="bi-x-circle" class="bi bi-x-circle" style="font-size:12px" /> {{ $t('cancel_transaction') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center" style="padding:2rem">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Transaction Info -->
      <div v-else-if="transaction && transaction.id">
        <!-- Tabs -->
        <ul class="nav nav-tabs detail-page-tabs mb-4">
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'overview'}" @click="step = 'overview'">{{$t('overview')}}</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'history'}" @click="step = 'history'">{{$t('history')}}</a>
          </li>
        </ul>

        <!-- Tab Content -->
        <!-- Overview Tab -->
        <div v-if="step === 'overview'">
          <div class="row g-4">
            <div class="col-12 col-xl-8">
              <section class="card transaction-overview-card mb-4">
                <div class="card-header bg-white d-flex justify-content-between align-items-center">
                  <div>
                    <span class="detail-card-eyebrow">{{ $t('transaction') }}</span>
                    <h6 class="mb-0"><AppIcon name="bi-receipt" class="me-2" />{{ $t('information') }}</h6>
                  </div>
                </div>
                <div class="detail-info-grid">
                  <div class="detail-info-item">
                    <span>{{ $t('reference') }}</span>
                    <strong>{{ transaction.reference }}</strong>
                  </div>
                  <div class="detail-info-item">
                    <span>{{ $t('date') }}</span>
                    <strong>{{ formatDate(transaction.created_at) }}</strong>
                  </div>
                  <div class="detail-info-item">
                    <span>{{ $t('cashier') }}</span>
                    <strong>{{ transaction.cashier ? transaction.cashier.name : '-' }}</strong>
                  </div>
                  <div class="detail-info-item">
                    <span>{{ $t('payment_method') }}</span>
                    <strong>{{ paymentMethodLabel }}</strong>
                  </div>
                </div>
              </section>

              <section class="card transaction-overview-card mb-4">
                <div class="card-header bg-white d-flex justify-content-between align-items-center">
                  <div>
                    <span class="detail-card-eyebrow">{{ transaction.products?.length || 0 }}</span>
                    <h6 class="mb-0"><AppIcon name="bi-bag" class="me-2" />{{ $t('products') }}</h6>
                  </div>
                </div>
                <div class="table-responsive transaction-products-table-wrap">
                  <table class="table transaction-products-table mb-0">
                    <thead>
                      <tr>
                        <th>{{ $t('product_label') }}</th>
                        <th class="text-center">{{ $t('product_quantity') }}</th>
                        <th class="text-end">{{ $t('product_price') }}</th>
                        <th class="text-end">{{ $t('total') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in transaction.products" :key="item.id">
                        <td>
                          <div class="product-name">{{ item.product?.label || item.name || '-' }}</div>
                          <small v-if="item.product?.reference" class="text-muted">{{ item.product.reference }}</small>
                        </td>
                        <td class="text-center fw-semibold">{{ item.quantity }}</td>
                        <td class="text-end">{{ formatMoney(item.unit_price) }}</td>
                        <td class="text-end fw-bold">{{ formatMoney(lineTotal(item)) }}</td>
                      </tr>
                      <tr v-if="!transaction.products || !transaction.products.length">
                        <td colspan="4" class="text-center text-muted py-4">{{ $t('no_products') }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section class="card transaction-overview-card">
                <div class="card-header bg-white d-flex justify-content-between align-items-center">
                  <h6 class="mb-0"><AppIcon name="bi-file-earmark-text" class="me-2 text-primary" />{{ $t('receipt_preview') }}</h6>
                </div>
                <div class="receipt-card-body">
                  <div class="receipt-preview-container">
                    <div class="receipt-preview" v-html="renderedReceipt"></div>
                  </div>
                </div>
              </section>
            </div>

            <aside class="col-12 col-xl-4">
              <section class="card transaction-overview-card financial-card mb-4">
                <div class="card-header bg-white d-flex justify-content-between align-items-center">
                  <h6 class="mb-0"><AppIcon name="bi-calculator" class="me-2" />{{ $t('summary') }}</h6>
                </div>
                <div class="financial-rows">
                  <div><span>{{ $t('subtotal') }}</span><strong>{{ formatMoney(transaction.sub_total) }}</strong></div>
                  <div><span>{{ $t('discount') }} <small>({{ transaction.discount || 0 }}%)</small></span><strong class="text-danger">-{{ formatMoney(transaction.total_discount || 0) }}</strong></div>
                  <div><span>{{ $t('vat') }}</span><strong>{{ formatMoney(transaction.total_vat) }}</strong></div>
                  <div class="financial-total"><span>{{ $t('total') }}</span><strong>{{ formatMoney(transaction.total_amount) }}</strong></div>
                </div>
              </section>

              <section class="card transaction-overview-card mb-4">
                <div class="card-header bg-white d-flex justify-content-between align-items-center">
                  <h6 class="mb-0"><AppIcon name="bi-person" class="me-2 text-primary" />{{ $t('client') }}</h6>
                </div>
                <div class="card-body">
                  <div v-if="!transaction?.client" class="empty-detail-state">{{ $t('no_client_found') }}</div>
                  <div v-else class="client-details">
                    <div><span>{{ $t('name') }}</span><strong>{{ transaction.client.name || '-' }}</strong></div>
                    <div><span>{{ $t('reference') }}</span><strong>{{ transaction.client.reference || '-' }}</strong></div>
                    <div><span>{{ $t('phone') }}</span><strong>{{ transaction.client.phone || '-' }}</strong></div>
                    <div><span>{{ $t('email') }}</span><strong>{{ transaction.client.email || '-' }}</strong></div>
                  </div>
                </div>
              </section>

              <section class="card transaction-overview-card">
                <div class="card-header bg-white d-flex justify-content-between align-items-center">
                  <h6 class="mb-0"><AppIcon name="bi-sticky" class="me-2 text-primary" />{{ $t('notes') }}</h6>
                </div>
                <div class="card-body">
                  <p class="mb-0 transaction-notes" :class="{ 'text-muted': !transaction.notes }">{{ transaction.notes || '-' }}</p>
                </div>
              </section>
            </aside>
          </div>
        </div>

        <!-- History Tab -->
        <div v-if="step === 'history'">
          <document-history
            :history="history"
            :loading="loadingHistory"
            :pagination="historyPagination"
            @pageChange="changeHistoryPage"
          />
        </div>
      </div>
    </div>

    <!-- Invoice Modal -->
    <add-edit-invoice-modal
      v-if="invoiceModalOpen"
      :action="'transfer'"
      :data="invoiceData"
      :isModalOpen="invoiceModalOpen"
      :modalId="'pos-to-invoice-modal'"
      :from="'pos'"
      @modalClosed="invoiceModalClosed"
    />
  </div>
</template>

<script>
import helper from '../../helper';
import AddEditInvoiceModal from '../../components/invoice/AddEditInvoiceModal.vue';
import DocumentHistory from '../../components/DocumentHistory.vue';

export default {
  name: 'PosTransactionDetails',
  components: {
    AddEditInvoiceModal,
    DocumentHistory
  },
  data() {
    return {
      transaction: {},
      loading: false,
      renderedReceipt: '',
      invoiceModalOpen: false,
      invoiceData: null,
      step: 'overview',
      loadingHistory: false,
      history: [],
      historyPagination: { count: 0, per_page: 10, current_page: 1, total_pages: 1 }
    };
  },
  computed: {
    business() {return this.$store.getters.BUSINESS;},
    helper(){ return helper},
    paymentMethodLabel() {
      const method = this.transaction?.payment_method;
      if (!method) return '-';
      const translated = this.$t(method);
      if (translated !== method) return translated;
      const label = String(method).replace(/_/g, ' ');
      return label.charAt(0).toUpperCase() + label.slice(1);
    }
  },
  methods: {
    formatMoney(number) { return helper.formatMoney(number);},
    lineTotal(item) {
      return (Number(item?.quantity) || 0) * (Number(item?.unit_price) || 0);
    },
    async fetchTransactionDetails() {
      try {
        this.loading = true;
        const transactionId = this.$route.params.id;
        const response = await helper.customGet(`${process.env.VUE_APP_API}/pos/${transactionId}`);
        this.transaction = response.data;
        this.loading = false;
        // Generate receipt preview after loading transaction
        await this.generateReceiptPreview();
        // Load history if tab is active
        if (this.step === 'history' && this.transaction && this.transaction.id) {
          this.getPosHistory(this.transaction.id)
        }
      } catch (error) {
        this.loading = false;
      }
    },
    async updateStatusToCanceled() {
      try {
        this.loading = true;
        await helper.customPatch(`${process.env.VUE_APP_API}/pos/${this.transaction.id}`, { payment_status: 'canceled' });
        this.transaction.payment_status = 'canceled';
        this.loading = false;
      } catch (error) {
        this.loading = false;
      }
    },
    confirmCancelTransaction() {
      this.$swal.fire({
        title: `${this.$t('are_you_sure')}?`,
        text: this.$t('you_cant_revert'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('yes_cancel_it')
      }).then((result) => {
        if (result.isConfirmed) {
          this.updateStatusToCanceled();
        }
      });
    },
    formatDate(date) {
      return helper.formatDate(date);
    },
    async loadReceiptTemplate() {
      try {
        const resp = await helper.customGet(`${process.env.VUE_APP_API}/templates`)
        if (resp?.data?.data?.length) {
          const existing = resp.data.data.find(t => (t.type === 'pos_receipt'))
          if (existing && existing.data) {
            let templateData = existing.data
            // Parse if it's a JSON string
            if (typeof templateData === 'string') {
              try {
                templateData = JSON.parse(templateData)
              } catch (e) {
                // If not JSON, treat as plain template text (legacy format)
                return templateData
              }
            }
            // Return the template portion if it's the new format
            if (templateData.template) {
              return templateData.template
            }
            // Otherwise return as-is
            return typeof templateData === 'string' ? templateData : JSON.stringify(templateData)
          }
        }
        return null
      } catch (e) {
        alert('Failed to load receipt template:', e)
        return null
      }
    },
    buildReceiptData() {
      if (!this.transaction) return {}
      
      // Calculate discount amount
      const subtotalValue = parseFloat(this.transaction.sub_total) || 0
      const discountPercent = parseFloat(this.transaction.discount) || 0
      const discountAmount = subtotalValue * (discountPercent / 100)
      const hasDiscount = discountPercent > 0
      
      return {
        business: {
          name: this.business?.name || 'My Business',
          address: this.business?.address || '123 Main St',
          phone: this.business?.phone || '+212 600 000 000',
          email: this.business?.email || 'contact@business.com',
          taxId: this.business?.tax_id || this.business?.ice || 'ICE123456789',
          logo: this.business?.logo || '',
        },
        receipt_id: this.transaction?.reference,
        pos_id: `${String(this.transaction?.reference).padStart(6, '0')}`,
        transaction_id: this.transaction.id,
        cashier: {
          id: this.transaction.cashier?.id || 1,
          name: this.transaction.cashier?.name || this.$t('cashier')
        },
        cashier_label: this.$t('cashier'),
        product_label: this.$t('product_label'),
        product_quantity: this.$t('product_quantity'),
        price_label: this.$t('product_price'),
        total_label: this.$t('total'),
        subtotal_label: this.$t('subtotal'),
        discount_label: this.$t('discount'),
        date: this.formatDate(this.transaction.created_at),
        client: this.transaction.client || { name: 'Walk-in Client' },
        subtotal: this.formatMoney(this.transaction.sub_total),
        discount: discountPercent > 0 ? `${discountPercent}%` : '0%',
        has_discount: hasDiscount,
        discount_amount: this.formatMoney(discountAmount),
        tax: this.formatMoney(this.transaction.total_vat),
        tax_rate: this.business?.vat ? `${this.business.vat}%` : '20%',
        total: this.formatMoney(this.transaction.total_amount),
        paid_amount: this.formatMoney(this.transaction.total_amount),
        change_amount: this.formatMoney(0),
        payment_method: this.transaction.payment_method || 'Cash',
        items: this.transaction.products?.map(item => ({
          name: item.product?.label || item.name,
          qty: item.quantity,
          price: this.formatMoney(item.unit_price),
          line_total: this.formatMoney(item.quantity * item.unit_price)
        })) || []
      }
    },
    renderTemplate(tpl, data) {
      if (!tpl) return ''
      let output = String(tpl)
      
      // Process items loop - matches {{#items}}...{{/items}}
      const loopRegex = /\{\{#items\}\}([\s\S]*?)\{\{\/items\}\}/g
      output = output.replace(loopRegex, (_match, inner) => {
        if (!Array.isArray(data.items)) return ''
        return data.items.map(item => this.replaceTokens(inner, Object.assign({}, data, item))).join('')
      })
      
      // Process discount conditional - matches {{#if_discount}}...{{/if_discount}}
      const discountRegex = /\{\{#if_discount\}\}([\s\S]*?)\{\{\/if_discount\}\}/g
      output = output.replace(discountRegex, (_match, inner) => {
        // Check both has_discount flag and discount value
        if (data.has_discount !== undefined) {
          return data.has_discount ? inner : ''
        }
        // Fallback: parse discount value
        const discountValue = parseFloat(String(data.discount || '0').replace(/[^0-9.-]/g, ''))
        return discountValue > 0 ? inner : ''
      })
      
      // Inject special components
      output = this.injectSpecialComponents(output, data)
      
      // Replace simple tokens
      output = this.replaceTokens(output, data)
      
      return output
    },
    
    injectSpecialComponents(html, data) {
      // Inject Logo
      if (data.business && data.business.logo) {
        html = html.replace(/\{\{LOGO\}\}/g, `<img src="${data.business.logo}" style="max-width:120px; max-height:80px; display:block; margin:0 auto 8px;">`)
      } else {
        html = html.replace(/\{\{LOGO\}\}/g, '')
      }
      
      // Inject Barcode
      const barcodeDataUrl = this.generateBarcode(data.pos_id || data.receipt_id)
      html = html.replace(/\{\{BARCODE\}\}/g, `<img src="${barcodeDataUrl}" style="max-width:100%; height:50px; display:block; margin:8px auto;">`)
      
      // Inject QR Code
      const qrData = `Receipt:${data.pos_id}\nTotal:${data.total}\nDate:${data.date}`
      const qrDataUrl = this.generateQRCode(qrData)
      html = html.replace(/\{\{QRCODE\}\}/g, `<img src="${qrDataUrl}" style="width:100px; height:100px; display:block; margin:8px auto;">`)
      
      return html
    },
    
    generateBarcode(text) {
      // Simple barcode generation using canvas
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        canvas.width = 200
        canvas.height = 50
        
        // Simple Code 128-like pattern (simplified for demo)
        ctx.fillStyle = 'white'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.fillStyle = 'black'
        
        // Draw bars
        let x = 10
        const barWidth = 2
        for (let i = 0; i < text.length; i++) {
          const code = text.charCodeAt(i)
          const pattern = code % 2 === 0 ? [1, 0, 1, 0, 1] : [1, 1, 0, 0, 1]
          pattern.forEach(bar => {
            if (bar === 1) {
              ctx.fillRect(x, 5, barWidth, 30)
            }
            x += barWidth
          })
        }
        
        // Draw text below barcode
        ctx.font = '10px monospace'
        ctx.textAlign = 'center'
        ctx.fillText(text, canvas.width / 2, 45)
        
        return canvas.toDataURL()
      } catch (e) {
        return ''
      }
    },
    
    generateQRCode(text) {
      // Simple QR code generation (basic pattern for demo)
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        const size = 100
        const moduleSize = 5
        canvas.width = size
        canvas.height = size
        
        ctx.fillStyle = 'white'
        ctx.fillRect(0, 0, size, size)
        ctx.fillStyle = 'black'
        
        // Create simple QR-like pattern
        for (let i = 0; i < size; i += moduleSize) {
          for (let j = 0; j < size; j += moduleSize) {
            // Create pseudo-random pattern based on text
            const hash = (text.charCodeAt((i + j) % text.length) + i + j) % 2
            if (hash === 0) {
              ctx.fillRect(i, j, moduleSize, moduleSize)
            }
          }
        }
        
        return canvas.toDataURL()
      } catch (e) {
        return ''
      }
    },
    replaceTokens(str, data) {
      return str.replace(/\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g, (m, key) => {
        const parts = key.split('.')
        let val = data
        for (const p of parts) {
          if (val && Object.prototype.hasOwnProperty.call(val, p)) {
            val = val[p]
          } else {
            return m
          }
        }
        return val || ''
      })
    },
    getDefaultReceiptTemplate() {
      return `
<div style="width:280px; margin:0 auto; font-family: Arial, sans-serif; color:#000">
  <div style="text-align:center; margin-bottom:8px">
    <div style="font-weight:bold">{{business.name}}</div>
    <div style="font-size:12px">{{business.address}}</div>
    <div style="font-size:12px">{{business.phone}}</div>
    <div style="font-size:12px; margin-top:4px">{{date}}</div>
  </div>
  <hr/>
  <div style="font-size:11px; margin-bottom:8px">
    <div style="display:flex; justify-content:space-between"><span>ID:</span><span>{{pos_id}}</span></div>
    <div style="display:flex; justify-content:space-between"><span>{{cashier_label}}:</span><span>{{cashier.name}}</span></div>
  </div>
  <hr/>
  <table style="width:100%; font-size:12px">
    <thead>
      <tr>
        <th style="text-align:left">{{product_label}}</th>
        <th style="text-align:center">{{product_quantity}}</th>
        <th style="text-align:right">{{price_label}}</th>
        <th style="text-align:right">{{total_label}}</th>
      </tr>
    </thead>
  <tbody>
    {{#items}}
    <tr>
      <td style="text-align:left">{{name}}</td>
      <td style="text-align:center">{{qty}}</td>
      <td style="text-align:right">{{price}}</td>
      <td style="text-align:right">{{line_total}}</td>
    </tr>
    {{/items}}
  </tbody>
</table>
<hr/>
<div style="font-size:12px">
  <div style="display:flex; justify-content:space-between"><span>Total HT:</span><span>{{subtotal}}</span></div>
  {{#if_discount}}<div style="display:flex; justify-content:space-between"><span>Remise:</span><span>{{discount}}</span></div>{{/if_discount}}
  <div style="display:flex; justify-content:space-between"><span>TVA:</span><span>{{tax}}</span></div>
  <div style="display:flex; justify-content:space-between; font-weight:bold"><span>Total TTC:</span><span>{{total}}</span></div>
</div>
<div style="text-align:center; margin-top:8px; font-size:12px"></div>
</div>`
    },
    async generateReceiptPreview() {
      try {
        // Load the saved POS receipt template
        let template = await this.loadReceiptTemplate()
        
        // If no template found, use default
        if (!template) {
          template = this.getDefaultReceiptTemplate()
        }
        
        // Prepare data for template rendering
        const templateData = this.buildReceiptData()
        
        // Render the template with data
        this.renderedReceipt = this.renderTemplate(template, templateData)
      } catch (error) {
        alert('Error generating receipt preview:', error)
        this.renderedReceipt = '<p>Error loading receipt preview</p>'
      }
    },
    async printReceipt() {
      try {
        // Load the saved POS receipt template
        let template = await this.loadReceiptTemplate()
        
        // If no template found, use default
        if (!template) {
          template = this.getDefaultReceiptTemplate()
        }
        
        // Prepare data for template rendering
        const templateData = this.buildReceiptData()
        
        // Render the template with data
        const renderedContent = this.renderTemplate(template, templateData)
        
        // Preferred: if running inside Electron, send to main for silent/background printing
        try {
          // contextBridge API via preload (recommended)
          if (window?.electron?.printReceipt) {
            const res = await window.electron.printReceipt({ html: renderedContent });
            if (!res || res.ok) return; // printed successfully
          }
          // Alternative naming fallback
          if (window?.electronAPI?.printReceipt) {
            await window.electronAPI.printReceipt({ html: renderedContent });
            return;
          }
          // Legacy direct ipcRenderer exposure (if injected)
          if (window?.ipcRenderer?.send) {
            window.ipcRenderer.send('print-receipt', { html: renderedContent });
            return;
          }
        } catch (e) {
          // If Electron call fails, continue to browser fallback
        }
        
        // Browser fallback: print via hidden iframe (opens dialog in browsers)
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.style.visibility = 'hidden';
        document.body.appendChild(iframe);
        const doc = iframe.contentWindow.document;
        doc.open();
        const html = [
          '<html>',
          '<head><style>@media print { @page { margin: 0; size: auto; } body { margin: 0; } }</style></head>',
          '<body>' + renderedContent + '</body>',
          '</html>'
        ].join('');
        doc.write(html);
        doc.close();
        // Give the iframe a tick to render, then print
        setTimeout(() => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          } finally {
            setTimeout(() => {
              if (iframe && iframe.parentNode) {
                iframe.parentNode.removeChild(iframe);
              }
            }, 1000);
          }
        }, 0);
        
      } catch (error) {
        alert('Error printing receipt:', error)
        alert('Error printing receipt')
      }
    },
    async transferToInvoice() {
      try {
        this.loading = true
        // Prepare invoice data from transaction
        this.invoiceData = {
          client_id: this.transaction.client_id || null,
          client: this.transaction.client || null,
          date: new Date().toISOString().split('T')[0],
          status: 'paid',
          payment_method: 'cash',
          amount_paid: this.transaction.total_amount,
          total: parseFloat(this.transaction.total_amount),
          sub_total: parseFloat(this.transaction.sub_total),
          total_vat: this.transaction.total_vat,
          total_discount: this.transaction.total_discount,
          notes: `Transferred from POS transaction ${this.transaction?.reference}`,
          pos_transaction_id: this.transaction.id, // Link to original POS transaction
            products: this.transaction.products?.map(product => {
            // Calculate discount amount per product
            const discountPercent = parseFloat(this.transaction.discount) || 0;
            const vatPercent = parseFloat(this.business.vat) || 0;
            const quantity = parseFloat(product.quantity) || 0;
            const unitPrice = parseFloat(product.unit_price) || 0;
            const purchasePrice = parseFloat(product.purchase_price) || 0;

            // Subtotal before discount and VAT
            const sub_total = quantity * unitPrice;

            // Discount amount
            const discountAmount = sub_total * (discountPercent / 100);

            // Subtotal after discount
            const afterDiscount = sub_total - discountAmount;

            // VAT amount
            const vatAmount = afterDiscount * (vatPercent / 100);

            // Final total
            const total = afterDiscount + vatAmount;

            return {
              product_id: product.product_id,
              inventory_id: product.inventory_id,
              product_label: product.product?.label || product.name,
              quantity: quantity,
              price: unitPrice,
              purchase_price: purchasePrice,
              discount: discountPercent,
              vat: vatPercent,
              total: parseFloat(total.toFixed(2)),
              sub_total: parseFloat(sub_total.toFixed(2)),
              custom_product: false
            }
            }) || []
        };
 
        const response = await helper.customPost(`${process.env.VUE_APP_API}/invoices`,{...this.invoiceData },{showToast:true})
        this.$router.push({path : `/invoices/${response.data.id}`})
        this.loading = false
        // Open the invoice modal
        // this.invoiceModalOpen = true;
        
      } catch (error) {
        alert('Error preparing invoice data:', error);
        this.$swal.fire({
          title: this.$t('error') || 'Error',
          text: this.$t('failed_to_prepare_invoice') || 'Failed to prepare invoice data',
          icon: 'error',
          confirmButtonText: this.$t('ok') || 'OK'
        });
      }
    },
    invoiceModalClosed() {
      this.invoiceModalOpen = false;
      this.invoiceData = null;
    },
    async getPosHistory(id){
      try{
        this.loadingHistory = true
        const response = await helper.customGet(`${process.env.VUE_APP_API}/pos/${id}/history`)
        this.history = response.data.data || response.data
        this.historyPagination = {
          count: response.data.count || 0,
          per_page: response.data.per_page || 10,
          current_page: response.data.current_page || 1,
          total_pages: response.data.total_pages || 1
        }
        this.loadingHistory = false
      }catch(error){
        alert('Error fetching history:', error)
        this.loadingHistory = false
      }
    },
    async changeHistoryPage(page){
      try{
        this.loadingHistory = true
        const response = await helper.customGet(`${process.env.VUE_APP_API}/pos/${this.transaction.id}/history?page=${page}`)
        this.history = response.data.data || response.data
        this.historyPagination = {
          count: response.data.count || 0,
          per_page: response.data.per_page || 10,
          current_page: response.data.current_page || 1,
          total_pages: response.data.total_pages || 1
        }
        this.loadingHistory = false
      }catch(error){
        alert('Error fetching history:', error)
        this.loadingHistory = false
      }
    },
  },
  watch: {
    step(newValue){
      if(newValue === 'history' && this.transaction && this.transaction.id){
        this.getPosHistory(this.transaction.id)
      }
    }
  },
  mounted() {
    window.mixpanel.track("pos_transaction_details");

    //check permissions
    if(helper.checkAccess('view_pos_transactions') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_pos_transactions') == -2) return this.$router.push({path : '/subscribe'})

    this.fetchTransactionDetails();
  }
};
</script>

<style scoped>
.transaction-overview-card {
  overflow: hidden;
  border: 1px solid #e4e9ef;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(35, 51, 70, 0.04);
}

.transaction-overview-card .card-header {
  min-height: 45px;
  padding: 11px 14px;
  border-bottom: 1px solid #edf1f5;
}

.transaction-overview-card .card-header h6 {
  display: flex;
  align-items: center;
  color: #29394d;
  font-size: 0.9rem;
  font-weight: 650;
}

.transaction-overview-card .card-header h6 > .app-lucide-icon {
  width: 18px;
  height: 18px;
  color: #3568d4;
}

.transaction-overview-card .card-body {
  padding: 10px 14px 14px;
}

.detail-card-eyebrow {
  display: block;
  color: #738094;
  font-size: 0.68rem;
  font-weight: 600;
}

.detail-info-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.detail-info-item {
  min-width: 0;
  padding: 12px 14px;
  border-right: 1px solid #edf1f5;
}

.detail-info-item:last-child { border-right: 0; }
.detail-info-item span,
.client-details span {
  display: block;
  margin-bottom: 0.3rem;
  color: #738094;
  font-size: 0.74rem;
  font-weight: 600;
}

.detail-info-item strong {
  display: block;
  overflow: hidden;
  color: #29394d;
  font-size: 0.86rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.transaction-products-table thead th {
  padding: 14px 18px;
  border-bottom: 1px solid #dfe4ea;
  background: #f8f9fb;
  color: #606d7e;
  font-size: 0.73rem;
  font-weight: 600;
  white-space: nowrap;
}

.transaction-products-table {
  min-width: 620px;
  margin: 0;
  overflow: hidden;
  border: 1px solid #e4e9ef;
  border-radius: 10px;
  border-collapse: separate;
  border-spacing: 0;
  color: #2d3b4f;
  font-size: 0.82rem;
}

.transaction-products-table-wrap {
  padding: 16px 18px 18px;
}

.transaction-products-table tbody td {
  padding: 16px 18px;
  border-color: #ebeff3;
  vertical-align: middle;
}

.product-name { color: #172033; font-weight: 700; }

.financial-rows { padding: 0.4rem 1.25rem 1.25rem; }
.financial-rows > div,
.client-details > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 0;
  border-bottom: 1px solid #edf1f5;
}

.financial-rows > div:last-child,
.client-details > div:last-child { border-bottom: 0; }
.financial-rows span { color: #64748b; }
.financial-rows strong { color: #172033; }
.financial-total {
  margin-top: 0.35rem;
  padding-top: 1rem !important;
  font-size: 1.15rem;
}
.financial-total strong { color: #0d6efd; font-size: 1.45rem; }

.client-details strong {
  max-width: 65%;
  color: #172033;
  overflow-wrap: anywhere;
  text-align: right;
}

.empty-detail-state {
  padding: 1rem;
  border-radius: 12px;
  background: #f8fafc;
  color: #64748b;
  text-align: center;
}

.receipt-card-body { padding: 1rem; background: #f8fafc; }

.receipt-preview-container {
  max-height: 520px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 6px;
  background: white;
}

.receipt-preview {
  display: flex;
  justify-content: center;
  padding: 1.25rem;
}

.receipt-preview table {
  border-collapse: collapse;
}

.receipt-preview hr {
  border: none;
  border-top: 1px solid #ccc;
  margin: 8px 0;
}

.transaction-notes {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

@media (max-width: 767px) {
  .detail-info-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .detail-info-item { border-bottom: 1px solid #edf1f5; }
  .detail-info-item:nth-child(2n) { border-right: 0; }
  .detail-info-item:nth-last-child(-n + 2) { border-bottom: 0; }
  .transaction-products-table-wrap { padding: 12px; }
  .receipt-preview { padding: 0.75rem; }
}

@media (max-width: 420px) {
  .detail-info-grid { grid-template-columns: 1fr; }
  .detail-info-item { border-right: 0; }
  .detail-info-item:nth-last-child(2) { border-bottom: 1px solid #edf1f5; }
}
</style>
